import HeroContent from 'components/HeroConsolidated/shared/HeroContent';
import HeroCTA from 'components/HeroConsolidated/shared/HeroCTA';
import Eyebrow from 'components/shared/Eyebrow/Eyebrow';
import Media from 'components/shared/Media/Media';
import HeroWrapper, { HeroContentWrapper } from 'components/HeroConsolidated/shared/HeroWrapper';
import { FullBleedVideoPlayer } from 'components/VideoPlayer';
import { HeroConsolidatedProps } from 'libs/contentful/props';
import { CSSProperties } from 'react';
import { cn, getColorway } from 'utils/styles';

const HeroFullBleedVariant = ({
  desktopMedia,
  mobileMedia,
  color,
  headline,
  copy,
  sys,
  showEmailInput,
  emailInputButtonText,
  cta,
  disclaimerModal,
  scrollCta,
  disclaimers,
  margins,
  sectionName,
  eyebrowAlternateLogo,
  eyebrowChimeLogo,
  eyebrowText,
  allowPromoCodeEntry,
  promoCodeInputButtonText,
  promoCodes,
  video,
  className,
  innerClassName,
  enrollmentUrl,
  mediaHasOverlay,
  heroIsScreenHeight,
}: HeroConsolidatedProps) => {
  const hasVideo = video != null && video.video != null; // Linked videos are not supported by this module variant
  const desktopUrl = hasVideo ? video.previewImage.url : desktopMedia?.url;
  const mobileUrl = hasVideo ? video.previewImage.url : (mobileMedia?.url ?? desktopMedia?.url);
  const primaryMedia = desktopMedia ?? mobileMedia;
  const hasMedia = primaryMedia != null;
  const useBackgroundImage = !mediaHasOverlay || hasVideo;

  return (
    <div
      style={
        {
          '--image-url': useBackgroundImage ? `url(${desktopUrl})` : '',
          '--mobile-image-url': useBackgroundImage ? `url(${mobileUrl})` : '',
        } as CSSProperties
      }
      className={cn(
        getColorway(color),
        'bg-transparent', // Transparent background so the play button gets right color
        'hero-container relative md:min-h-125 max-sm:min-h-150 w-full h-full',
        {
          'bg-[image:var(--mobile-image-url)] sm:bg-[image:var(--image-url)] bg-center bg-cover': useBackgroundImage,
        },
        className,
      )}>
      {(hasVideo || mediaHasOverlay) && (
        <div className={cn('absolute w-full h-full overflow-hidden')}>
          {mediaHasOverlay && <div className={cn(getColorway(color), 'absolute top-0 left-0 w-full h-full')}></div>}
          {hasVideo && <FullBleedVideoPlayer video={video} mediaHasOverlay={mediaHasOverlay} />}
          {hasMedia && !hasVideo && (
            <div className="h-full opacity-15">
              <Media
                media={primaryMedia}
                mobileMedia={mobileMedia}
                sizes={'100vw'}
                className="w-full h-full rounded-none"
              />
            </div>
          )}
        </div>
      )}
      <HeroWrapper
        testId={'fullBleedVariant'}
        className={cn(
          'bg-inherit drop-shadow-[0px_4px_4px_rgba(5,35,22,0.75)] max-sm:min-h-150 flex',
          { 'sm:min-h-[80vh] md:min-h-[84vh] sm:items-center': heroIsScreenHeight },
          innerClassName,
        )}
        color={color}
        margins={margins}
        sectionName={sectionName}>
        <HeroContentWrapper className="sm:max-w-1/2 h-full flex flex-col">
          <div className="flex flex-col-reverse">
            <HeroContent headline={headline} copy={copy} sys={sys} />
            <Eyebrow
              eyebrowAlternateLogo={eyebrowAlternateLogo}
              eyebrowChimeLogo={eyebrowChimeLogo}
              eyebrowText={eyebrowText}
              color={color}
              isHeading={true}
              sys={sys}
            />
          </div>
          <HeroCTA
            showEmailInput={showEmailInput}
            sys={sys}
            emailInputButtonText={emailInputButtonText}
            cta={cta}
            disclaimerModal={disclaimerModal}
            disclaimers={disclaimers}
            scrollCta={scrollCta}
            allowPromoCodeEntry={allowPromoCodeEntry}
            promoCodeInputButtonText={promoCodeInputButtonText}
            promoCodes={promoCodes}
            enrollmentUrl={enrollmentUrl}
            className={'flex-end'}
          />
        </HeroContentWrapper>
      </HeroWrapper>
    </div>
  );
};

export default HeroFullBleedVariant;
